import { useParams } from 'react-router-dom'
import useCostCategories from '@/hooks/useCostCategories'
import { thermostatsCollection } from '@/reducers/thermostats'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { useThermostatInactivityPeriodsCollection } from '@/reducers/thermostatInactivityPeriods'
import { inactiveComfortSettingsCollection } from '@/reducers/inactiveComfortSettings'
import { thermostatActiveSettingsCollection } from '@/reducers/thermostatActiveSettings'
import { getInactivePeriodSummarySubtitleLines } from '@/app/pages/thermostat/cost-categories/utils/inactivityPeriodFormatting'
import { getGridSupportSettingsSummary } from '@/app/pages/utility-program/unenroll/suggestions/components/ThermostatSuggestionCard/ThermostatSuggestionCard.utils'
import { unenrollmentThermostatDisplayName } from '@/app/pages/utility-program/unenroll/utils'
import { Thermostat, ThermostatSettings } from '@/types'
import { ActiveThermostatSettings } from '@/types/activeThermostatSettings'

export default function useConfigureThermostatViewModel() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const thermostatId = Number(deviceId)

  const { data: thermostats, isLoadingOrIdle: thermostatsLoading } =
    thermostatsCollection.useFetch()
  const thermostat = thermostats.find((t) => t.id === thermostatId) as
    | Thermostat
    | undefined

  const { costCategories, isLoading: costCategoriesLoading } =
    useCostCategories(thermostatId)

  const { data: settingsArray, isLoadingOrIdle: settingsLoading } =
    thermostatSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/settings/`,
    )
  const settings = settingsArray[0] as ThermostatSettings | undefined

  const { data: comfortSettings, isLoadingOrIdle: comfortSettingsLoading } =
    thermostatComfortSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/comfort_settings/`,
    )

  const firstComfortSettingId = comfortSettings[0]?.id
  const { data: inactiveSchedules, isLoadingOrIdle: inactiveSchedulesLoading } =
    inactiveComfortSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/comfort_settings/${
        firstComfortSettingId ?? ''
      }/inactive/`,
      { require: Boolean(firstComfortSettingId) },
    )

  const { data: inactivityPeriods, isLoadingOrIdle: inactivityPeriodsLoading } =
    useThermostatInactivityPeriodsCollection(thermostatId)

  const { data: activeSettingsArray, isLoadingOrIdle: activeSettingsLoading } =
    thermostatActiveSettingsCollection.useFetch(deviceId)
  const activeSettings = activeSettingsArray[0] as
    | ActiveThermostatSettings
    | undefined

  const isLoadingOrIdle =
    thermostatsLoading ||
    costCategoriesLoading ||
    settingsLoading ||
    comfortSettingsLoading ||
    inactiveSchedulesLoading ||
    inactivityPeriodsLoading ||
    activeSettingsLoading ||
    !thermostat

  const displayName = thermostat
    ? unenrollmentThermostatDisplayName(thermostat)
    : ''

  const inactiveTimesSubtitleLines = thermostat
    ? getInactivePeriodSummarySubtitleLines({
        thermostat,
        inactivityPeriods,
        comfortSettings,
        inactiveComfortSettings: inactiveSchedules,
      })
    : []

  const gridSupportSummary = thermostat
    ? getGridSupportSettingsSummary(comfortSettings, thermostat)
    : []

  const saveMoneyPrescribed =
    activeSettings?.cost_category_optimization_enabled.is_program_prescribed ??
    false
  const saveMoneyEnabled =
    activeSettings?.cost_category_optimization_enabled.value ?? false
  // When the program prescribes Save Money, the active-setting `value`
  // field carries the required direction (true = required ON, false =
  // required OFF). Programs like PNM prescribe certain goals OFF.
  const saveMoneyRequiredDirection: 'on' | 'off' = saveMoneyEnabled
    ? 'on'
    : 'off'

  // Pre-cool is unavailable for Ecobee thermostats — the toggle on the
  // precool page is forced off and disabled, so mirror that here.
  const isEcobee = thermostat?.portal === 'Ecobee'
  const precoolEnabled = !isEcobee && (settings?.pre_cool_enabled ?? false)

  const settingPaths = {
    gridSupport: `/devices/thermostat/${thermostatId}/grid-support`,
    precool: `/devices/thermostat/${thermostatId}/precool-preheat-settings`,
    costCategories: `/devices/thermostat/${thermostatId}/cost-categories`,
  }

  return {
    isLoadingOrIdle,
    thermostat,
    displayName,
    settingPaths,
    costCategories,
    inactiveTimesSubtitleLines,
    gridSupportEnabled: settings?.grid_support_enabled ?? false,
    gridSupportSummary,
    precoolEnabled,
    saveMoneyPrescribed,
    saveMoneyEnabled,
    saveMoneyRequiredDirection,
  }
}
